import React, { useEffect, useState } from 'react';
import { territoryService } from '../services/api';
import { ScopeTree } from '../types';

// The Territory / Domain pulldowns, shared by every page that lists scoped
// data. One component so the option lists, the wildcard convention and the
// "your account only reaches here" behaviour are identical everywhere —
// three pages each rolling their own is how two of them end up subtly
// disagreeing about what an empty selection means.
//
// Conventions:
//   value ''  = "All" at that level. Sent as absent, which the API reads as
//               "everything in bounds", so the caller never has to special-case
//               the wildcard string.
//   A level the account cannot choose (a domain user has exactly one domain)
//   renders as static text rather than a select with a single option.
//
// Selecting a territory resets the domain: a domain from the previous
// territory is meaningless under the new one and would 403.

// Loaded once per mount of a page. Small, and it changes only when an
// administrator adds a territory, so a cached module-level promise keeps the
// several pages that use this from each issuing their own request.
let treePromise: Promise<ScopeTree> | null = null;

export function loadScopeTree(force = false): Promise<ScopeTree> {
  if (force || !treePromise) {
    treePromise = territoryService.tree();
  }
  return treePromise;
}

export interface ScopeSelection {
  territory?: string;
  domain?: string;
}

interface Props {
  value: ScopeSelection;
  onChange: (next: ScopeSelection) => void;
  // Shown after the pulldowns — e.g. a licence note for a read-only territory.
  children?: React.ReactNode;
  // Hides the whole control when the account has no choice to make at either
  // level. Off by default: even a domain user benefits from seeing where they
  // are, and a label is cheaper than wondering.
  hideWhenFixed?: boolean;
}

const ScopeFilter: React.FC<Props> = ({ value, onChange, children, hideWhenFixed = false }) => {
  const [tree, setTree] = useState<ScopeTree | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadScopeTree()
      .then(t => { if (!cancelled) setTree(t); })
      .catch(() => { if (!cancelled) setError('Could not load territories'); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <span className="small">{error}</span>;
  }
  if (!tree) {
    return <span className="small">Loading scope…</span>;
  }

  const fixed = !tree.can_select_territory && !tree.can_select_domain;
  if (fixed && hideWhenFixed) {
    return null;
  }

  // Which territory's domains to offer. With "All territories" selected there
  // is no single domain list to show, so the domain pulldown is suppressed
  // rather than offering a merged list whose entries could collide between
  // territories.
  const selectedTerritory = value.territory || (tree.can_select_territory ? '' : tree.territory);
  const territory = tree.territories.find(t => t.name === selectedTerritory) || null;

  const rowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '10px', flexWrap: 'wrap',
  };
  const labelStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  };

  return (
    <div style={rowStyle}>
      <label className="small" style={labelStyle}>
        Territory
        {tree.can_select_territory ? (
          <select
            value={value.territory || ''}
            onChange={e => onChange({ territory: e.target.value || undefined, domain: undefined })}
          >
            <option value="">All territories</option>
            {tree.territories.map(t => (
              <option key={t.name} value={t.name}>
                {t.display_name}{t.is_readonly ? ' (read-only)' : ''}
              </option>
            ))}
          </select>
        ) : (
          <strong>{tree.territory || '—'}</strong>
        )}
      </label>

      {territory && (
        <label className="small" style={labelStyle}>
          Domain
          {tree.can_select_domain ? (
            <select
              value={value.domain || ''}
              onChange={e => onChange({
                territory: value.territory,
                domain: e.target.value || undefined,
              })}
            >
              <option value="">All domains ({territory.domains.length})</option>
              {territory.domains.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          ) : (
            <strong>{tree.domain || '—'}</strong>
          )}
        </label>
      )}

      {territory?.is_readonly && (
        <span className="small" title={territory.description || undefined}>
          ⚗ Read-only territory — non-commercial research data, no audio.
        </span>
      )}

      {children}
    </div>
  );
};

export default ScopeFilter;
